import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import {
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState, useEffect } from "react";
import axios from "utils/api";
import { countParentLeavesAfterAllowance } from "utils/parentLeaveCount";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import Table from "examples/Tables/Table";
import ArgonBadge from "components/ArgonBadge";
import PropTypes from "prop-types";

function Tables({ classData }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [student, setStudent] = useState("");
  const [cycle, setCycle] = useState("");

  const cyclesList = classData?.cycles || [];
  const studentsList = classData?.students || [];

  const studentOptions = studentsList.map((s) => s.name || s.fullName || "N/A");
  const cycleOptions = cyclesList.map((c, index) => c.name || `Cycle ${index + 1}`);

  useEffect(() => {
    if (classData?._id) fetchAttendanceData();
  }, [classData?._id]);

  useEffect(() => {
    if (cyclesList.length > 0 && !cycle) {
      const currentCycle =
        classData?.currentCycle ||
        classData?.currentCycleName ||
        cyclesList.find((item) => item?.isCurrent)?.name ||
        cyclesList.find((item) => item?.status === "ONGOING")?.name ||
        cyclesList[0]?.name ||
        "Cycle 1";

      setCycle(currentCycle);
    }
  }, [classData, cycle]);

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`/attendance`, {
        params: { classSessionId: classData._id },
      });

      if (response.data.success) {
        const attendances = response.data.attendances || response.data.data || [];
        setAttendanceData(attendances);
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const selectedCycleIndex = cyclesList.findIndex(
    (c) =>
      String(c?.name || "")
        .trim()
        .toLowerCase() ===
      String(cycle || "")
        .trim()
        .toLowerCase()
  );

  const safeCycleIndex = selectedCycleIndex >= 0 ? selectedCycleIndex : 0;
  const selectedCycleData = cyclesList[safeCycleIndex] || null;

  const sortedInvoices = [...(classData?.invoices || [])].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  const normalizePaymentStatus = (status) => {
    const value = String(status || "")
      .trim()
      .toUpperCase();

    if (!value) return "PENDING";
    if (value === "SUCCESS") return "PAID";
    if (value === "COMPLETED") return "PAID";

    if (["ONGOING", "UPCOMING", "CREATED", "SCHEDULED"].includes(value)) {
      return "PENDING";
    }

    return value;
  };

  const toSafeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const getCycleNumber = (cycleData, cycleIdx) =>
    Number(String(cycleData?.name || "").match(/\d+/)?.[0]) || cycleIdx + 1;

  const normalizeCycleName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase();

  const getInvoiceCycleNumber = (invoice) => {
    const sourceCycleNumber = Number(invoice?.sourceCycleNumber);
    if (Number.isFinite(sourceCycleNumber) && sourceCycleNumber > 0) return sourceCycleNumber;

    const cycleNumber = Number(invoice?.cycleNumber);
    if (Number.isFinite(cycleNumber) && cycleNumber > 0) return cycleNumber;

    const sourceCycleIndex = Number(invoice?.sourceCycleIndex);
    if (Number.isFinite(sourceCycleIndex) && sourceCycleIndex >= 0) return sourceCycleIndex + 1;

    return null;
  };

  const invoiceHasCycleMetadata = (invoice) =>
    getInvoiceCycleNumber(invoice) !== null || Boolean(normalizeCycleName(invoice?.cycleName));

  const findInvoiceForCycle = (cycleData, cycleIdx) => {
    const cycleNumber = getCycleNumber(cycleData, cycleIdx);
    const cycleName = normalizeCycleName(cycleData?.name || `Cycle ${cycleNumber}`);

    const exactMatch = sortedInvoices.find((invoice) => {
      const invoiceCycleNumber = getInvoiceCycleNumber(invoice);
      const invoiceCycleName = normalizeCycleName(invoice?.cycleName);

      return invoiceCycleNumber === cycleNumber || (invoiceCycleName && invoiceCycleName === cycleName);
    });

    if (exactMatch) return exactMatch;

    const hasAnyCycleMetadata = sortedInvoices.some(invoiceHasCycleMetadata);
    return hasAnyCycleMetadata ? null : sortedInvoices[cycleIdx] || null;
  };

  const selectedInvoice = selectedCycleData ? findInvoiceForCycle(selectedCycleData, safeCycleIndex) : null;
  const selectedCycleAmount = toSafeNumber(
    selectedCycleData?.monthlyFee || selectedCycleData?.paymentAmount || selectedInvoice?.totalAmount
  );
  const explicitCyclePackageAmount = cyclesList.reduce(
    (sum, cycleItem) => sum + toSafeNumber(cycleItem?.monthlyFee || cycleItem?.paymentAmount),
    0
  );
  const totalCyclePackageAmount =
    explicitCyclePackageAmount > 0
      ? explicitCyclePackageAmount
      : cyclesList.length > 0 && selectedCycleAmount > 0
      ? cyclesList.length * selectedCycleAmount
      : 0;
  const fullPackagePaidInvoice =
    totalCyclePackageAmount > 0
      ? sortedInvoices.find(
          (invoice) =>
            normalizePaymentStatus(invoice?.status) === "PAID" &&
            !invoiceHasCycleMetadata(invoice) &&
            toSafeNumber(invoice?.totalAmount) >= totalCyclePackageAmount
        )
      : null;

  const selectedCyclePaymentStatus = normalizePaymentStatus(
    fullPackagePaidInvoice?.status ||
      selectedCycleData?.paymentStatus ||
      selectedCycleData?.financeStatus ||
      selectedCycleData?.feeStatus ||
      selectedCycleData?.invoiceStatus ||
      selectedInvoice?.status ||
      classData?.paymentStatus
  );

  const getAttendanceCycleIndex = (attendance, index) => {
    if (attendance?.cycleNumber) return Number(attendance.cycleNumber) - 1;

    if (attendance?.cycleName) {
      return cyclesList.findIndex(
        (c) =>
          String(c?.name || "")
            .trim()
            .toLowerCase() ===
          String(attendance.cycleName || "")
            .trim()
            .toLowerCase()
      );
    }

    const total = Number(selectedCycleData?.totalNoOfClasses || selectedCycleData?.classes || 0);
    if (!total) return 0;

    return Math.floor(index / total);
  };

  const filteredAttendanceData = attendanceData.filter((att, index) => {
    const matchStudent =
      !student ||
      student === "No students" ||
      att?.studentId?.name === student ||
      att?.student?.name === student ||
      att?.studentName === student;

    const attendanceCycleIndex = getAttendanceCycleIndex(att, index);

    const matchCycle = !cycle || cycle === "No cycles" || attendanceCycleIndex === safeCycleIndex;

    return matchStudent && matchCycle;
  });

  const stats = {
    total: filteredAttendanceData.length,
    present: filteredAttendanceData.filter((a) => {
      const types = Array.isArray(a.type) ? a.type : [a.type];
      return !types.includes("ABSENT");
    }).length,
    absent: filteredAttendanceData.filter((a) => {
      const types = Array.isArray(a.type) ? a.type : [a.type];
      return types.includes("ABSENT");
    }).length,
    regular: filteredAttendanceData.filter((a) =>
      Array.isArray(a.type) ? a.type.includes("REGULAR") : a.type === "REGULAR"
    ).length,
    extra: filteredAttendanceData.filter((a) =>
      Array.isArray(a.type) ? a.type.includes("EXTRA") : a.type === "EXTRA"
    ).length,
    recovery: filteredAttendanceData.filter((a) =>
      Array.isArray(a.type) ? a.type.includes("RECOVERY") : a.type === "RECOVERY"
    ).length,
    leave: countParentLeavesAfterAllowance(filteredAttendanceData),
  };

  const completedClasses = selectedCycleData?.completedClasses ?? stats.present ?? 0;

  const totalClasses =
    selectedCycleData?.totalNoOfClasses ||
    selectedCycleData?.classes ||
    classData?.totalNoOfClasses ||
    completedClasses ||
    0;

  const remainingClasses = Math.max(0, totalClasses - completedClasses);

  const sortedAttendances = filteredAttendanceData
    .filter((attendance) => attendance.attendanceTime)
    .slice()
    .sort((a, b) => new Date(a.attendanceTime).getTime() - new Date(b.attendanceTime).getTime());

  const firstAttendance = sortedAttendances[0];
  const lastAttendance = sortedAttendances[sortedAttendances.length - 1];

  const data = {
    completedSessions: completedClasses,
    totalSessions: totalClasses,
    remainingSessions: remainingClasses,
    extraClasses: stats.extra,
    recoveryClasses: stats.recovery,
    startDate: firstAttendance
      ? new Date(firstAttendance.attendanceTime).toLocaleDateString("en-IN")
      : classData?.firstClassOn
      ? new Date(classData.firstClassOn).toLocaleDateString("en-IN")
      : "N/A",
    endDate: lastAttendance
      ? new Date(lastAttendance.attendanceTime).toLocaleDateString("en-IN")
      : "N/A",
    classStatus: classData?.status || "CREATED",
    cyclePaymentStatus: selectedCyclePaymentStatus,
    remark:
      totalClasses === 0
        ? "Total classes not set. Please update class session details."
        : "Attendance tracking in progress",
  };

  const progress = data.totalSessions > 0 ? (data.completedSessions / data.totalSessions) * 100 : 0;

  const tableData = {
    columns: [
      { name: "sno", align: "center" },
      { name: "date", align: "left" },
      { name: "startTime", align: "left" },
      { name: "endTime", align: "left" },
      { name: "duration", align: "left" },
      { name: "type", align: "center" },
      { name: "status", align: "center" },
      { name: "topic", align: "left" },
      { name: "homework", align: "left" },
    ],

    rows: filteredAttendanceData.map((att, index) => ({
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {index + 1}
        </ArgonTypography>
      ),
      date: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.startTime
            ? new Date(att.startTime).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A"}
        </ArgonTypography>
      ),
      startTime: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.startTime
            ? new Date(att.startTime).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A"}
        </ArgonTypography>
      ),
      endTime: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.attendanceTime
            ? new Date(att.attendanceTime).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "Ongoing"}
        </ArgonTypography>
      ),
      duration: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.duration
            ? `${att.duration} min`
            : att.startTime && att.attendanceTime
            ? `${Math.round(
                (new Date(att.attendanceTime) - new Date(att.startTime)) / (1000 * 60)
              )} min`
            : "N/A"}
        </ArgonTypography>
      ),
      type: (() => {
        const types = Array.isArray(att.type) ? att.type : [att.type];
        const isAbsent = types.includes("ABSENT");
        const label = types.length > 0 ? types.join(", ") : "REGULAR";
        const color = isAbsent
          ? "error"
          : types.includes("EXTRA")
          ? "info"
          : types.includes("RECOVERY")
          ? "warning"
          : "success";

        return (
          <ArgonBadge variant="gradient" badgeContent={label} color={color} size="xs" container />
        );
      })(),
      status: (() => {
        const hasEarlyAttendanceNote =
          typeof att.earlyAttendanceNote === "string" &&
          att.earlyAttendanceNote.trim() !== "";
        const isIncomplete = att.earlyAttendance === true || hasEarlyAttendanceNote;

        return (
          <ArgonBadge
            variant="gradient"
            badgeContent={
              isIncomplete ? "Incomplete" : att.attendanceTime ? "Completed" : "Ongoing"
            }
            color={isIncomplete ? "error" : att.attendanceTime ? "success" : "warning"}
            size="xs"
            container
          />
        );
      })(),
      topic: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.topicCovered || "N/A"}
        </ArgonTypography>
      ),
      homework: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {att.homeWorkStatus ? "Assigned" : "Not Assigned"}
        </ArgonTypography>
      ),
    })),
  };

  return (
    <ArgonBox mb={3}>
      <Card>
        <ArgonBox p={3}>
          <ArgonTypography mb={2} variant="h6" fontWeight="medium">
            Attendance
          </ArgonTypography>

          {/* Filter Controls */}
          <ArgonBox
            sx={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              p: 3,
              border: "1px solid #e0e0e0",
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              mb: 3,
            }}
          >
            <ArgonBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
              {/* Student Dropdown */}
              <ArgonBox>
                <ArgonTypography variant="body2" fontWeight="medium" color="text.primary" mb={1}>
                  Select Student
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={student}
                  options={studentOptions.length > 0 ? studentOptions : ["No students"]}
                  onChange={(e) => setStudent(e.target.value)}
                  width={200}
                />
              </ArgonBox>

              {/* Cycle Dropdown */}
              <ArgonBox>
                <ArgonTypography variant="body2" fontWeight="medium" color="text.primary" mb={1}>
                  Select Cycle
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={cycle}
                  options={cycleOptions.length > 0 ? cycleOptions : ["No cycles"]}
                  width={200}
                  onChange={(e) => setCycle(e.target.value)}
                />
              </ArgonBox>
            </ArgonBox>
          </ArgonBox>

          {/* Attendance Card */}
          <Card
            sx={{
              borderRadius: "20px",
              background: "linear-gradient(135deg, #f5f7fa 0%, #eef1f5ff 100%)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.1)",
              p: 4,
              border: "1px solid #e0e0e0",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative Background Element */}
            <ArgonBox
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                background: "linear-gradient(45deg, #efefefff, #f2f2f2ff)",
                borderRadius: "50%",
                opacity: 0.1,
              }}
            />

            {/* Header */}
            <ArgonBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={4}
              flexWrap="wrap"
            >
              <ArgonBox display="flex" alignItems="center" gap={2}>
                <ArgonBox
                  sx={{
                    background: "linear-gradient(45deg, #e2e2e2ff, #764ba2)",
                    borderRadius: "12px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  {/* <CalendarToday sx={{ color: "white", fontSize: "20px" }} /> */}
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="medium">
                    Attendance Overview
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#6c757d">
                    Track your learning progress
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <ArgonBox display="flex" gap={2} flexWrap="wrap">
                <ArgonBox display="flex" alignItems="center" gap={1}>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#6c757d">
                    Class Status:
                  </ArgonTypography>
                  <Chip
                    label={data.classStatus === "SCHEDULED" ? "Active" : data.classStatus}
                    sx={{
                      fontSize: 12,
                      fontWeight: "bold",
                      color: "white !important",
                      background: "linear-gradient(45deg, #4CAF50, #45a049)",
                      borderRadius: "12px",
                      px: 2,
                      boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
                    }}
                  />
                </ArgonBox>

                <ArgonBox display="flex" alignItems="center" gap={1}>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#6c757d">
                    Cycle Payment:
                  </ArgonTypography>
                  <Chip
                    label={data.cyclePaymentStatus}
                    sx={{
                      fontSize: 12,
                      fontWeight: "bold",
                      color: "white !important",
                      background: "linear-gradient(45deg, #FF9800, #F57C00)",
                      borderRadius: "12px",
                      px: 2,
                      boxShadow: "0 4px 15px rgba(255, 152, 0, 0.3)",
                    }}
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            {/* Progress Section */}
            <ArgonBox
              sx={{
                background: "white",
                borderRadius: "16px",
                p: 3,
                mb: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <ArgonBox display="flex" alignItems="center" gap={2} mb={2}>
                {/* <TrendingUp sx={{ color: "#4CAF50", fontSize: "20px" }} /> */}
                <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                  Session Progress
                </ArgonTypography>
              </ArgonBox>

              {data.totalSessions === 0 ? (
                <ArgonBox
                  sx={{
                    background: "#fff3cd",
                    borderRadius: "8px",
                    p: 2,
                    border: "1px solid #ffc107",
                  }}
                >
                  <ArgonTypography variant="body2" color="warning" fontWeight="medium">
                    ⚠️ Total classes not configured. Current completed classes:{" "}
                    {data.completedSessions}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <>
                  <ArgonBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <ArgonTypography variant="body2" fontWeight="medium" color="#6c757d">
                      Sessions Completed: {data.completedSessions}/{data.totalSessions}
                    </ArgonTypography>
                    <ArgonTypography variant="h6" fontWeight="bold" color="#4CAF50">
                      {Math.round(progress)}%
                    </ArgonTypography>
                  </ArgonBox>

                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      background: "#e0e0e0",
                      width: "100%",
                      "& .MuiLinearProgress-bar": {
                        background: "linear-gradient(45deg, #4CAF50, #45a049)",
                        borderRadius: 6,
                        width: `${progress}%`,
                      },
                    }}
                  />
                </>
              )}
            </ArgonBox>

            {/* Details Grid */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={2.4}>
                <ArgonBox
                  sx={{
                    background: "white",
                    borderRadius: "12px",
                    p: 2.5,
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mb={1}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                      Total Sessions
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#2196F3">
                    {data.totalSessions}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <ArgonBox
                  sx={{
                    background: "white",
                    borderRadius: "12px",
                    p: 2.5,
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mb={1}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                      Regular Sessions
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#4CAF50">
                    {stats.regular}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <ArgonBox
                  sx={{
                    background: "white",
                    borderRadius: "12px",
                    p: 2.5,
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mb={1}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                      Extra Sessions
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#FF9800">
                    {stats.extra}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <ArgonBox
                  sx={{
                    background: "white",
                    borderRadius: "12px",
                    p: 2.5,
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mb={1}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                      Recovery Sessions
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#F44336">
                    {stats.recovery}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <ArgonBox
                  sx={{
                    background: "white",
                    borderRadius: "12px",
                    p: 2.5,
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <ArgonBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mb={1}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                      Leave Sessions
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonTypography variant="body2" fontWeight="medium" color="#9C27B0">
                    {stats.leave}
                  </ArgonTypography>
                </ArgonBox>
              </Grid>
            </Grid>

            {/* Remark Section */}
            <ArgonBox
              sx={{
                background: "white",
                borderRadius: "16px",
                p: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              }}
            >
              <ArgonBox
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
              >
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  {/* <Comment sx={{ color: "#9C27B0", fontSize: "20px" }} /> */}
                  <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                    Remarks
                  </ArgonTypography>
                </ArgonBox>

                {/* <ArgonButton
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={handleDeleteClick}
                >
                  Edit Remark
                </ArgonButton> */}
              </ArgonBox>

              <ArgonTypography
                variant="body2"
                color="medium"
                sx={{
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  p: 1,
                  border: "1px solid #e9ecef",
                  fontStyle: "italic",
                }}
              >
                {data.remark}
              </ArgonTypography>
            </ArgonBox>
          </Card>

          <Card
            sx={{
              borderRadius: "20px",
              border: "1px solid #e0e0e0",
              position: "relative",
              overflow: "hidden",
              mt: 3,
            }}
          >
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <ArgonTypography variant="h6" fontWeight="medium">
                  Attendance List
                </ArgonTypography>
              </ArgonBox>

              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {loading ? (
                  <ArgonBox display="flex" justifyContent="center" p={5}>
                    <ArgonTypography variant="h6">Loading attendance...</ArgonTypography>
                  </ArgonBox>
                ) : filteredAttendanceData.length === 0 ? (
                  <ArgonBox display="flex" justifyContent="center" p={5}>
                    <ArgonTypography variant="h6" color="text">
                      No attendance records found
                    </ArgonTypography>
                  </ArgonBox>
                ) : (
                  <Table columns={tableData.columns} rows={tableData.rows} />
                )}
              </ArgonBox>
            </ArgonBox>
          </Card>
        </ArgonBox>
      </Card>
    </ArgonBox>
  );
}
Tables.propTypes = {
  classData: PropTypes.shape({
    _id: PropTypes.string,
    status: PropTypes.string,
    paymentStatus: PropTypes.string,
    currentCycle: PropTypes.string,
    currentCycleName: PropTypes.string,
    totalNoOfClasses: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    firstClassOn: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),

    invoices: PropTypes.arrayOf(
      PropTypes.shape({
        status: PropTypes.string,
        totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      })
    ),

    cycles: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        status: PropTypes.string,
        isCurrent: PropTypes.bool,
        totalNoOfClasses: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        classes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        completedClasses: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        paymentStatus: PropTypes.string,
        financeStatus: PropTypes.string,
        feeStatus: PropTypes.string,
        invoiceStatus: PropTypes.string,
      })
    ),

    students: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        fullName: PropTypes.string,
      })
    ),
  }),
};
export default Tables;
